// src/SubscribeCheckout.js
import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Layout from "./Components/Layout";
import { api } from "./lib/api";
import { useAuth } from "./auth/AuthContext";
import { COUNTRIES, nameForISO2 } from "./Components/checkout/country-iso2";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const PLAN_META = {
  monthly: { label: "Monthly", priceLabel: "$6 / month", totalLabel: "$6.00", amount: 6 },
  yearly: {
    label: "Yearly",
    priceLabel: "$36 / year",
    subMonthlyLabel: "$3 / month",
    totalLabel: "$36.00",
    amount: 36,
  },
  lifetime: { label: "Lifetime", priceLabel: "$89.99 lifetime", totalLabel: "$89.99", amount: 89.99 },
};

const REQUIRED_FIELDS = ["firstName", "lastName", "email", "address1", "city", "state", "postcode", "country"];

export default function SubscribeCheckout() {
  const q = useQuery();
  const nav = useNavigate();
  const loc = useLocation();
  const { user, loading, setUser } = useAuth();

  const planRaw = (q.get("plan") || "monthly").toLowerCase();
  const plan = PLAN_META[planRaw] ? planRaw : "monthly";
  const meta = PLAN_META[plan];

  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postcode: "",
    country: "AU", // store ISO2
  });

  const isAuthed = !!user?.id;
  const displayUserName = user?.username || user?.name || user?.email || "";

  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [errors, setErrors] = useState({});

  // ✅ login-gate logic
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [emailCheckedValue, setEmailCheckedValue] = useState("");

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: false }));
    if (errMsg) setErrMsg("");
  };

  const goLogin = () => {
    const next = `${loc.pathname}${loc.search || ""}`;
    nav(`/login?next=${encodeURIComponent(next)}`);
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (e) {
      // ignore
    }
    try {
      setUser(null);
    } catch (e) {
      // ignore
    }
  };

  const isEmailValid = (email) => /^\S+@\S+\.\S+$/.test(String(email || "").trim());

  const checkEmailExists = async (rawEmail) => {
    if (user) {
      setEmailExists(false);
      setCheckingEmail(false);
      return false;
    }

    const email = String(rawEmail || "").trim().toLowerCase();
    if (!email || !isEmailValid(email)) {
      setEmailExists(false);
      setEmailCheckedValue("");
      setCheckingEmail(false);
      return false;
    }

    if (emailCheckedValue && emailCheckedValue === email) return emailExists;

    try {
      setCheckingEmail(true);
      const { data } = await api.get(`/api/auth/check-email?email=${encodeURIComponent(email)}`);
      const exists = !!data?.exists;

      setEmailExists(exists);
      setEmailCheckedValue(email);

      return exists;
    } catch (e) {
      setEmailExists(false);
      setEmailCheckedValue("");
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    REQUIRED_FIELDS.forEach((key) => {
      if (!String(form[key] || "").trim()) nextErrors[key] = true;
    });

    if (String(form.email || "").trim() && !isEmailValid(form.email)) {
      nextErrors.email = true;
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setErrMsg("Please fill in the required fields.");
      return false;
    }

    return true;
  };

  // Prefill if logged in
  useEffect(() => {
    if (!user?.id) return;

    setForm((p) => ({
      ...p,
      email: user?.email || p.email,
      firstName: user?.first_name || p.firstName,
      lastName: user?.last_name || p.lastName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Debounced email existence check
  useEffect(() => {
    if (user) return;

    const raw = String(form.email || "");
    if (!raw) {
      setEmailExists(false);
      setEmailCheckedValue("");
      return;
    }

    const t = setTimeout(() => {
      checkEmailExists(raw);
    }, 450);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, user]);

  const onSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErrMsg("");

    if (!user && emailExists) {
      setErrMsg("This email is already registered. Please log in to continue.");
      return;
    }

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const total_cents = Math.round(Number(meta.amount) * 100);

      const { data } = await api.post("/api/payments/create-checkout-session", {
        total_cents,
        email: String(form.email || "").trim().toLowerCase(),
        firstName: String(form.firstName || "").trim(),
        lastName: String(form.lastName || "").trim(),
        phone: String(form.phone || "").trim(),
        address1: String(form.address1 || "").trim(),
        address2: String(form.address2 || "").trim(),
        city: String(form.city || "").trim(),
        state: String(form.state || "").trim(),
        postcode: String(form.postcode || "").trim(),
        country: String(form.country || "AU").trim().toUpperCase(),
        plan,
      });

      if (!data?.ok || !data?.url) throw new Error(data?.message || "Could not start checkout.");

      window.location.href = data.url;
    } catch (e2) {
      setErrMsg(e2?.response?.data?.message || e2?.message || "Checkout failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout imageUrl="https://mychinesehoroscope.com/my-chinese-horoscope-finder.png">
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px 44px" }}>
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "center",
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: 18,
            padding: "14px 16px",
            marginBottom: 18,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 12, background: "#fc9c02" }} />
            <div>
              <div style={{ fontWeight: 600, lineHeight: 1.1 }}>My Chinese Horoscope</div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Secure checkout</div>
            </div>
          </div>

          {!isAuthed ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 28, fontWeight: 700, textAlign: "left" }}>
  {isAuthed ? `${displayUserName}` : ""}
</div>
              <button
                type="button"
                onClick={goLogin}
                style={{
                  minWidth: 180,
                  height: 44,
                  borderRadius: 12,
                  border: "none",
                  background: "#004e8d",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: "pointer",
                  lineHeight: "20px",
                }}
              >
                Login
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              
               <button
    type="button"
    onClick={() => nav("/dashboard")}
    style={{
      height: 40,
      borderRadius: 12,
      border: "none",
      background: "#fc9c02",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
      padding: "0 16px",
    }}
  >
    My Account Dashboard
  </button>
            </div>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.65fr 1fr", gap: 18, alignItems: "start" }}>
          {/* LEFT */}
          <form
            onSubmit={onSubmit}
            style={{
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 8,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 23, fontWeight: 600, textAlign: "left" }}>
  {isAuthed ? "Account checkout" : "Guest Checkout"}
</div>

{isAuthed && (
  <div style={{ fontSize: 14, opacity: 0.7, marginTop: 4 }}>
    Logged in as {displayUserName}
  </div>
)}

            <div style={{ marginTop: 10, fontWeight: 600 }}>
              Plan: <span style={{ fontWeight: 1000 }}>{meta.label}</span> — {meta.totalLabel}{" "}
              <span style={{ opacity: 0.7 }}>{meta.priceLabel}</span>
            </div>

            {meta.subMonthlyLabel ? (
              <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: "#fc9c02" }}>
                Equivalent: {meta.subMonthlyLabel}
              </div>
            ) : null}

            {errMsg ? (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 12,
                  background: "rgba(255,0,0,0.08)",
                  border: "1px solid rgba(255,0,0,0.25)",
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {errMsg}
              </div>
            ) : null}

            {!user && emailExists ? (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 12,
                  background: "rgba(255, 180, 0, 0.12)",
                  border: "1px solid rgba(255, 180, 0, 0.35)",
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                This email is already registered. Please{" "}
                <button
                  type="button"
                  onClick={goLogin}
                  style={{
                    fontWeight: 600,
                    textDecoration: "underline",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  log in
                </button>{" "}
                to continue.
              </div>
            ) : null}

            <div style={{ height: 14 }} />

            <FieldRow2>
              <Field
                label="First name"
                value={form.firstName}
                onChange={(v) => set("firstName", v)}
                error={errors.firstName}
                autoComplete="given-name"
              />
              <Field
                label="Last name"
                value={form.lastName}
                onChange={(v) => set("lastName", v)}
                error={errors.lastName}
                autoComplete="family-name"
              />
            </FieldRow2>

            <Space />

            <FieldRow2>
              <Field
                label="Email"
                value={form.email}
                onChange={(v) => set("email", v)}
                error={errors.email}
                autoComplete="email"
                inputMode="email"
                hint={!user && checkingEmail ? "Checking account…" : ""}
              />
              <Field
                label="Phone"
                value={form.phone}
                onChange={(v) => set("phone", v)}
                error={false}
                autoComplete="tel"
                inputMode="tel"
              />
            </FieldRow2>

            <Space />

            <Field
              label="Address line 1"
              value={form.address1}
              onChange={(v) => set("address1", v)}
              error={errors.address1}
              autoComplete="address-line1"
            />

            <Space />

            <Field
              label="Address line 2"
              value={form.address2}
              onChange={(v) => set("address2", v)}
              error={false}
              autoComplete="address-line2"
            />

            <Space />

            <FieldRow3>
              <Field
                label="City"
                value={form.city}
                onChange={(v) => set("city", v)}
                error={errors.city}
                autoComplete="address-level2"
              />
              <Field
                label="State"
                value={form.state}
                onChange={(v) => set("state", v)}
                error={errors.state}
                autoComplete="address-level1"
              />
              <Field
                label="Postcode"
                value={form.postcode}
                onChange={(v) => set("postcode", v)}
                error={errors.postcode}
                autoComplete="postal-code"
                inputMode="numeric"
              />
            </FieldRow3>

            <Space />

            <div style={{ maxWidth: 260 }}>
              <CountryField
  value={form.country}
  onChange={(v) => set("country", v)}
  error={errors.country}
/>
            </div>

            <div style={{ marginTop: 14, fontSize: 13, opacity: 0.75 }}>
              Payments are processed securely by Stripe Checkout (stripe.com).
            </div>

            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                   <Link to="/Aboutus" style={{ textDecoration: "underline" }}>
                About
              </Link>
    {" · "}
              <Link to="/Privacy-Policy" style={{ textDecoration: "underline" }}>
                Privacy Policy
              </Link>
              {" · "}
              <Link to="/Contact" style={{ textDecoration: "underline" }}>
                Contact
              </Link>
          
         
            </div>

            <div style={{ display: "flex", gap: 18, marginTop: 14, fontWeight: 900, fontSize: 14 }}>
              <button
                type="button"
                onClick={() => nav("/subscribe")}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#fc9c02",
                  padding: 0,
                }}
              >
                ← Change plan
              </button>

              <button
                type="button"
                onClick={() => nav("/")}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  color: "#000",
                  padding: 0,
                }}
              >
                Back to home
              </button>
            </div>
          </form>

          {/* RIGHT */}
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 6,
              padding: 18,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
              <div>Cart</div>
              <div style={{ fontSize: 12, opacity: 0.7, fontWeight: 600 }}>1 item</div>
            </div>

            <div style={{ borderTop: "1px solid rgba(0,0,0,0.12)", margin: "12px 0" }} />

            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 1000 }}>{meta.label} Subscription</div>
                <div style={{ fontSize: 12, opacity: 0.7 }}>{meta.priceLabel}</div>
              </div>
              <div style={{ fontWeight: 1000 }}>{meta.totalLabel}</div>
            </div>

            <div style={{ borderTop: "1px solid rgba(0,0,0,0.12)", margin: "12px 0" }} />

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 18 }}>
              <div style={{ fontWeight: 600 }}>Total</div>
              <div style={{ fontWeight: 600 }}>{meta.totalLabel}</div>
            </div>

            <div style={{ height: 12 }} />

            <button
              type="button"
              onClick={onSubmit}
              disabled={submitting || loading || (!user && emailExists)}
              style={{
                width: "100%",
                borderRadius: 14,
                border: "none",
                padding: "12px 14px",
                background: !user && emailExists ? "rgba(0,0,0,0.2)" : "#fc9c02",
                color: "#fff",
                fontWeight: 600,
                cursor: !user && emailExists ? "not-allowed" : "pointer",
              }}
              title={!user && emailExists ? "Please log in to continue" : ""}
            >
              {submitting ? "Redirecting…" : "Checkout"}
            </button>

            <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
              {!user ? "Guest checkout is available." : "You can continue with your account."}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ---------- UI helpers ---------- */

function Space() {
  return <div style={{ height: 12 }} />;
}

function FieldRow2({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}

function FieldRow3({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>{children}</div>;
}

function Field({ label, value, onChange, error, autoComplete, inputMode, hint }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        inputMode={inputMode}
        style={{
          width: "100%",
          borderRadius: 12,
          border: `1px solid ${error ? "#ff6161" : "rgba(0,0,0,0.18)"}`,
          padding: "10px 12px",
          outline: "none",
        }}
      />
      {hint ? <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>{hint}</div> : null}
    </label>
  );
}

/**
 * Country dropdown using your `COUNTRIES` structure:
 * { name: "Australia", code: "AU" }
 */
function CountryField({ value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filtered, setFiltered] = useState(() => COUNTRIES);

  useEffect(() => {
    const q = String(query || "").trim().toLowerCase();

    if (!q) {
      setFiltered(COUNTRIES);
      return;
    }

    setFiltered(
      COUNTRIES.filter((c) => {
        const nm = String(c?.name || "").toLowerCase();
        const code = String(c?.code || "").toLowerCase();
        return nm.includes(q) || code.includes(q);
      })
    );
  }, [query]);

  const selectedName = String(value || ""); // ✅ value is full name

  return (
    <div style={{ position: "relative" }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Country</div>

      <input
        value={open ? query : selectedName}
        onFocus={() => {
          setOpen(true);
          setQuery("");
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        placeholder="Select country"
        autoComplete="country"
        style={{
          width: "100%",
          borderRadius: 8,
          border: `1px solid ${error ? "#ff6161" : "rgba(0,0,0,0.18)"}`,
          padding: "10px 12px",
          outline: "none",
        }}
      />

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            maxHeight: 220,
            overflowY: "auto",
            background: "#fff",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 8,
            marginTop: 4,
            zIndex: 50,
          }}
        >
          {filtered.map((c) => (
            <div
              key={c.code}
              onMouseDown={() => {
                onChange(String(c.name || "")); // ✅ save full name only
                setOpen(false);
                setQuery("");
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 14,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{c.name}</span>
              <span style={{ opacity: 0.6, fontSize: 12, fontWeight: 600 }}>({c.code})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}